<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useWebStreamWs } from '@/ws/web-stream.ws'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxDropDownButton } from 'devextreme-vue'

import { getImportJobActions, IMPORT_ACTION, useColumns } from '@/composables/registry/service-import-grid'
import * as dataGrid from '@/composables/data-grid'
import { getI18n } from '@/plugins/i18n'
import { useDialog } from '@/composables/dialog'

import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import ChipStatus from '@/components/shared/ChipStatus.vue'
import ImportExportJobWizard from '@/components/registry/ImportExportJobWizard.vue'
import ImportExportJobResult from '@/components/registry/ImportExportJobResult.vue'

import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

const gridId = 'service-import-grid'

const { gridRef } = dataGrid.useGridRef()

const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const importStatus = portalStore.importExportStatusEnum
const i18n = getI18n()
const { genericDialog } = useDialog()

const showImportError = (message) => genericDialog(i18n.t('common.errorMessage'), i18n.te(message) ? i18n.t(message) : message, { ok: true })

const openFile = (job) => {
  if (job.source_file) window.open(`${window.config.BASE_MEDIA}${job.source_file}`, '_blank')
}

const selectedRows = ref([])
const deletableSelectedIds = computed(() => selectedRows.value.filter(row => ![importStatus.RUNNING, importStatus.DRY_RUN_RUNNING, importStatus.DONE].includes(row.status)).map(row => row.id))
const updatingJobIds = ref(new Set())
const completedRunIds = new Set()
const latestRunIds = new Map()

const actionItems = (status) => getImportJobActions(status).map(action => ({ action, text: i18n.t(`common.importExportAction.${action}`) }))
const onJobAction = async(job, action) => {
  if (!getImportJobActions(job.status).includes(action) || updatingJobIds.value.has(job.id)) {
    return
  }
  updatingJobIds.value.add(job.id)
  try {
    const controlAction = action === IMPORT_ACTION.STOP
    const response = await (controlAction ? registryStore.api.controlImportExportJobAsync(job.id, action) : registryStore.api.runImportExportJobAsync(job.id, action === IMPORT_ACTION.DRY_RUN))
    if (disposed) return
    if (response.data.run_id < (latestRunIds.get(job.id) || 0)) return
    latestRunIds.set(job.id, response.data.run_id)
    if (!completedRunIds.has(response.data.run_id)) {
      dataSource.store().push([{ type: 'update', key: job.id, data: { status: response.data.status, cancel_requested: !!response.data.stopping } }])
    }
  } catch (error) {
    showImportError(error.message || i18n.t('common.error'))
  } finally {
    updatingJobIds.value.delete(job.id)
  }
}

const webStreamWs = useWebStreamWs()
let progressSubscription
let disposed = false
const onImportProgress = (sender, response, message) => {
  const progress = message.data
  if (disposed || !progress?.run_id || completedRunIds.has(progress.run_id) || progress.run_id < (latestRunIds.get(progress.job_id) || 0)) return
  latestRunIds.set(progress.job_id, progress.run_id)
  dataSource.store().push([{ type: 'update', key: progress.job_id, data: { status: progress.status, read_count: progress.read_count } }])
  if ([importStatus.DONE, importStatus.DRY_RUN_COMPLETED, importStatus.CANCELLED, importStatus.ERROR].includes(progress.status)) {
    completedRunIds.add(progress.run_id)
    dataSource.store().push([{ type: 'update', key: progress.job_id, data: { cancel_requested: false } }])
    registryStore.api.getImportExportJobsAsync({ $filter: `id eq ${progress.job_id}` }).then(response => {
      if (!disposed && latestRunIds.get(progress.job_id) === progress.run_id && response.data[0]) dataSource.store().push([{ type: 'update', key: progress.job_id, data: response.data[0] }])
    })
  }
}
onMounted(async() => {
  progressSubscription = await webStreamWs.subscribeAsync(
    [webStreamWs.IMPORT_PROGRESS_STREAM], onImportProgress,
  )
  if (disposed) progressSubscription?.unsubscribe()
  else if (!i18n.te('common.importExportAction.stop')) await portalStore.setLanguageAsync(i18n.locale.value, true)
})
onUnmounted(() => {
  disposed = true
  progressSubscription?.unsubscribe()
})

const toggleJobAsync = async(job) => {
  if (updatingJobIds.value.has(job.id)) return
  updatingJobIds.value.add(job.id)
  try {
    await dataSource.store().update(job.id, {
      status: job.status === importStatus.DISABLED ? importStatus.READY : importStatus.DISABLED,
    })
    await gridRef.value.instance.refresh()
  } catch (error) {
    await showImportError(error.message || i18n.t('common.error'))
  } finally {
    updatingJobIds.value.delete(job.id)
  }
}

const showWizard = ref(false)
const resultJob = ref(null)

const columns = useColumns({
  onViewResult: (job) => { resultJob.value = job },
  onToggleJob: toggleJobAsync,
  isJobUpdating: (id) => updatingJobIds.value.has(id),
})

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
}

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, {
    useDefaultAddButton: true,
    allowBatchDelete: true,
  })

  const items = e.toolbarOptions.items
  const addBtnIndex = items.findIndex(i => i.name === 'addRowButton')
  if (addBtnIndex !== -1) {
    items[addBtnIndex] = {
      widget: 'dxButton',
      location: 'after',
      name: 'addCustomRowButton',
      options: {
        icon: 'add',
        stylingMode: 'text',
        onClick: () => { showWizard.value = true },
      },
    }
  }

  items.push(dataGrid.toolbarClearFilterIcon(e.component))
  items.push(dataGrid.toolbarRefreshIcon(e.component))
}

const editing = dataGrid.useDefaultEditing(null, {}, {
  allowUpdating: false,
  allowDeleting: true,
})

const batchDeleteAsync = (selectedIds) => registryStore.api.batchDeleteImportExportJobsAsync(selectedIds)

const dataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.importExportJobsUrl, {
  customParamsFn: () => { return { $top: 500 } },
  avoidDuplicateDatasourceIds: true,
})

const stateStoring = dataGrid.useDefaultStateStoring(gridId)
const loadGridState = () => {
  const state = stateStoring.customLoad()
  if (!state) return state
  return {
    ...state,
    columns: state.columns?.map(column => {
      if (column.type === 'buttons' || (!column.dataField && column.visibleIndex === 0)) return { ...column, width: '8rem' }
      if (column.dataField === 'error_message') return { ...column, width: '4rem' }
      if (column.dataField === 'source_file_name') return { ...column, width: undefined }
      return column
    }),
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  stateStoring: { ...stateStoring, customLoad: loadGridState },
  dataSource: dataSource,
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  height: '100%',
  rowDragging: {
    allowReordering: false,
  },
  onInitialized: (e) => dataGrid.useDefaultOnInitialized(e, {enableGrouping: true}),
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
})
</script>

<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #importJobActionTemplate="{ data: cellInfo }">
        <dx-drop-down-button
          :items="actionItems(cellInfo.data.status)"
          :disabled="updatingJobIds.has(cellInfo.data.id) || cellInfo.data.cancel_requested"
          :drop-down-options="{ width: 220 }"
          :hint="i18n.t('common.importExportActions')"
          :show-arrow-icon="false"
          display-expr="text"
          icon="mdi mdi-dots-horizontal"
          styling-mode="text"
          @item-click="onJobAction(cellInfo.data, $event.itemData.action)"
        />
      </template>
      <template #importExportStatusCellTemplate="{ data: cellInfo }">
        <chip-status :data="portalStore.getLocalizedImportExportStatuses().find(x => x.id === cellInfo.value)" />
      </template>
      <template #importErrorTemplate="{ data: cellInfo }">
        <q-btn
          v-if="cellInfo.value"
          icon="mdi-alert-circle-outline"
          color="negative"
          flat
          round
          size="sm"
          @click.stop="showImportError(cellInfo.value)"
        >
          <q-tooltip>{{ i18n.t('common.error') }}</q-tooltip>
        </q-btn>
      </template>
      <template #attachmentFileNameTemplate="{ data: cellInfo }">
        <div>
          <a
            v-if="cellInfo.data.source_file"
            href="#"
            style="text-decoration: underline"
            @click.prevent="openFile(cellInfo.data)"
          >
            {{ cellInfo.data.source_file_name }}
          </a>
          <span v-else>{{ cellInfo.data.source_file_name }}</span>
        </div>
      </template>
      <template #customToolbarDeleteButtonTemplate>
        <custom-toolbar-delete-button-template
          :selected-rows-id="deletableSelectedIds"
          :grid="gridRef"
          :on-delete="batchDeleteAsync"
          :disabled="deletableSelectedIds.length === 0"
        />
      </template>
    </dx-data-grid>
    <import-export-job-result
      v-if="resultJob"
      :job="resultJob"
      @close="resultJob = null"
    />
    <import-export-job-wizard
      v-model:visible="showWizard"
      @created="gridRef.instance.refresh()"
    />
  </div>
</template>
